import React, { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../services';
import { Button, Input } from '../components/common';
import './SignupPage.css';

export const SignupPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<'signup' | 'verify'>('signup');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const navigate = useNavigate();

  const handleSignup = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!acceptedTerms) {
      setError('Please accept the Terms & Conditions to continue');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    try {
      await authService.signUp(username, password, email);
      setStep('verify');
    } catch (err: any) {
      setError(err.message || 'Sign up failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await authService.confirmSignUp(username, verificationCode);
      navigate('/login', { 
        state: { 
          message: 'Account verified! Please sign in.',
          termsAcceptedDuringSignup: true 
        } 
      });
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-container">
        {step === 'signup' ? (
          <>
            <div className="signup-header">
              <h1 className="signup-title">Create Account</h1>
              <p className="signup-subtitle">
                Join ElevatePhysique AI for personalized health insights
              </p>
            </div>

            <form className="signup-form" onSubmit={handleSignup}>
              {error && <div className="signup-error">{error}</div>}

              <Input
                label="Username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Choose a username"
                fullWidth
                required
              />

              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                fullWidth
                required
              />

              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password (min. 8 characters)"
                fullWidth
                required
                showPasswordToggle
              />

              <Input
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                fullWidth
                required
                showPasswordToggle
              />

              <div className="terms-checkbox">
                <label className="terms-label">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                  />
                  <span>
                    I accept the{' '}
                    <button
                      type="button"
                      className="terms-link"
                      onClick={() => setShowTermsModal(true)}
                    >
                      Terms & Conditions
                    </button>
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                variant="primary"
                fullWidth
                isLoading={isLoading}
              >
                Create Account
              </Button>
            </form>

            <p className="signup-footer">
              Already have an account?{' '}
              <Link to="/login" className="signup-link">
                Sign in
              </Link>
            </p>
          </>
        ) : (
          <>
            <div className="signup-header">
              <h1 className="signup-title">Verify Email</h1>
              <p className="signup-subtitle">
                We've sent a verification code to {email}
              </p>
            </div>

            <form className="signup-form" onSubmit={handleVerify}>
              {error && <div className="signup-error">{error}</div>}

              <Input
                label="Verification Code"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                fullWidth
                required
              />

              <Button
                type="submit"
                variant="primary"
                fullWidth
                isLoading={isLoading}
              >
                Verify Account
              </Button>
            </form>

            <p className="signup-footer">
              Didn't receive the code?{' '}
              <button
                type="button"
                className="signup-link-btn"
                onClick={() => setStep('signup')}
              >
                Try again
              </button>
            </p>
          </>
        )}
      </div>

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h2>Terms & Conditions</h2>
              <button
                className="terms-modal-close"
                onClick={() => setShowTermsModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="terms-modal-content">
              <div className="terms-warning">
                <span className="terms-warning-icon">⚠️</span>
                <strong>Important Medical Disclaimer</strong>
              </div>
              
              <p>By using ElevatePhysique AI, you acknowledge and agree to the following:</p>
              
              <ol className="terms-list">
                <li>
                  <strong>AI Limitations:</strong> This application uses artificial intelligence to provide health and fitness information. AI systems can make mistakes and may provide inaccurate, incomplete, or inappropriate recommendations.
                </li>
                <li>
                  <strong>Not Medical Advice:</strong> The information provided by ElevatePhysique AI is for informational purposes only and should NOT be considered as medical advice, diagnosis, or treatment recommendations.
                </li>
                <li>
                  <strong>Consult Healthcare Professionals:</strong> Always consult with a qualified, certified healthcare professional (doctor, physician, registered dietitian, certified personal trainer, etc.) before starting any new exercise program, diet plan, or making health-related decisions.
                </li>
                <li>
                  <strong>Emergency Situations:</strong> In case of a medical emergency, call your local emergency services immediately. Do not rely on this application for emergency medical guidance.
                </li>
                <li>
                  <strong>Personal Responsibility:</strong> You are solely responsible for any actions you take based on the information provided by this application. Use the information at your own risk.
                </li>
                <li>
                  <strong>Application Under Development:</strong> This application is currently under development. Features, accuracy, and reliability may vary and improve over time.
                </li>
              </ol>

              <div className="terms-highlight">
                <strong>🏥 Best Practice:</strong> Always verify any health or fitness advice with a real, certified healthcare professional before implementing changes to your lifestyle.
              </div>
            </div>
            <div className="terms-modal-footer">
              <Button
                variant="primary"
                onClick={() => {
                  setAcceptedTerms(true);
                  setShowTermsModal(false);
                }}
              >
                I Understand & Accept
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
